import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEnGb from '@angular/common/locales/en-GB';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  provideZoneChangeDetection
} from '@angular/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withRouterConfig } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { authInterceptor, DXCAuthService } from '@dxc/data-access';
import { provideIcons } from '@ng-icons/core';
import Aura from '@primeng/themes/aura';
import { icons } from '@shared/ui';
import {
  provideAngularQuery,
  QueryClient
} from '@tanstack/angular-query-experimental';
import { provideZxvbnServiceForPSM } from 'angular-password-strength-meter/zxcvbn';
import { providePopoverConfig } from 'ng-primitives/popover';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';

// All potentially supported locales
registerLocaleData(localeEnGb);
registerLocaleData(localeEn);

// Default locale
const DEFAULT_LOCALE = 'en-GB';

/**
 * Factory function to determine the locale based on browser settings.
 * Falls back to DEFAULT_LOCALE if the browser locale isn't supported
 */
function getLocaleFactory(): string {
  const platformId = inject(PLATFORM_ID);

  if (
    isPlatformBrowser(platformId)
    && typeof navigator !== 'undefined'
    && navigator.language
  ) {
    const browserLocale = navigator.language;
    const supportedLocales = ['en-GB', 'en-US'];
    const supportedLanguages = ['en'];

    if (supportedLocales.includes(browserLocale)) {
      return browserLocale;
    }

    // Try matching the base language (e.g. 'en' from 'en-GB')
    const baseLanguage = browserLocale.split('-')[0];
    if (supportedLanguages.includes(baseLanguage)) {
      const matchingLocale = supportedLocales.find((loc) =>
        loc.startsWith(baseLanguage)
      );

      // Fallback if no specific locale found for the base lang
      return matchingLocale || DEFAULT_LOCALE;
    }
  }

  // Fallback if locale detection fails
  return DEFAULT_LOCALE;
}

export function tokenGetter() {
  const dxcAuthService = inject(DXCAuthService);
  return dxcAuthService.getAuthToken();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideTranslateService({ lang: 'en', fallbackLang: 'en' }),
    provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json', useHttpBackend: true }),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: tokenGetter,
          // TODO: update jwt config
          allowedDomains: ['example.com'],
          disallowedRoutes: ['http://example.com/examplebadroute/']
        }
      })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimationsAsync(),
    provideIcons(icons),
    provideAngularQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 3 // 3 minutes
          }
        }
      })
    ),
    QueryClient,
    provideZxvbnServiceForPSM(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          cssLayer: { name: 'primeng', order: 'theme, base, primeng' }
        }
      }
    }),
    { provide: LOCALE_ID, useFactory: getLocaleFactory },
    providePopoverConfig({ closeOnOutsideClick: false })
  ]
};
